/**
 * "Land all ready" (docs/ux/workflow-simplification.md B6, docs/e2e.md "Fewer passkey
 * ceremonies"): every ready landing in the inbox, approved with ONE passkey ceremony. Each
 * command is still its own signed envelope; each daemon verifies its own step-up item.
 */

import type { SignedCommand } from '@viko/crypto'
import { isFullSha, type LandGrantProof } from '@viko/protocol'
import type { CommandSigner, SignOptions } from './signer.js'
import type { CommandOutcome, InboxItem } from './types.js'

export interface ReadyLanding {
  item: InboxItem
  machineId: string
  branch: string
}

/** Landings that can go in one batch: user-approved `land` cards with a machine (not ships). */
export function readyLandings(items: readonly InboxItem[]): ReadyLanding[] {
  const out: ReadyLanding[] = []
  for (const item of items) {
    if (item.type !== 'land' || !item.land || item.category === 'ship-tap') continue
    const machineId =
      item.machineId ?? (item.body as { machineId?: string } | null)?.machineId ?? undefined
    if (!machineId) continue
    out.push({ item, machineId, branch: item.land.branch || 'branch' })
  }
  return out.slice(0, 32)
}

export interface LandAllResult {
  landed: InboxItem[]
  failed: { item: InboxItem; error: unknown }[]
}

/** Sign all (one ceremony), then deliver each; one failure never blocks the others. */
export async function landAll(
  deps: {
    signer: Pick<CommandSigner, 'signBatch'>
    deliver: (signed: SignedCommand) => Promise<CommandOutcome>
    /** land-grant: the standing grant covering a landing, if any (those need no tap). */
    grantFor?: (r: ReadyLanding) => LandGrantProof | undefined
  },
  ready: readonly ReadyLanding[],
): Promise<LandAllResult> {
  const summary = `Land ${ready.length} branch${ready.length === 1 ? '' : 'es'} into main: ${ready
    .map((r) => r.branch)
    .join(', ')}`
  const opts: SignOptions = { summary }
  const signed = await deps.signer.signBatch(
    ready
      .map((r) => {
        const landGrant = deps.grantFor?.(r)
        return { r, opts: landGrant ? { ...opts, landGrant } : opts }
      })
      .map(({ r, opts }) => ({
        machineId: r.machineId,
        payload: {
          workstreamId: r.item.land!.workstreamId,
          ...(r.item.runId ? { runId: r.item.runId } : {}),
          ...(r.item.land!.branch ? { branch: r.item.land!.branch } : {}),
          // g18-stack: each approval covers exactly the HEAD its card shows.
          ...(isFullSha(r.item.land!.headSha) ? { headSha: r.item.land!.headSha } : {}),
          target: 'main' as const,
        },
        opts,
      })),
    summary,
  )
  const result: LandAllResult = { landed: [], failed: [] }
  await Promise.all(
    signed.map(async (s, i) => {
      const item = ready[i]!.item
      try {
        const out = await deps.deliver(s)
        if (out.status === 'rejected' || out.status === 'failed')
          result.failed.push({ item, error: new Error(out.reason ?? out.error ?? out.status) })
        else result.landed.push(item)
      } catch (error) {
        result.failed.push({ item, error })
      }
    }),
  )
  return result
}
